import { For } from '../c-mp/comp/For'
import { $when, Show, type IShowThenProps } from '../c-mp/comp/Show'
import { Slot } from '../c-mp/comp/Slot'
import { defineComponent } from '../c-mp/fun/defineComponent'
import { url } from '../c-mp/fun/url'
import { useEffect } from '../c-mp/fun/useEffect'
import { useMemo } from '../c-mp/fun/useMemo'
import { mutateState, useState } from '../c-mp/fun/useState'
import { formatNumber } from '../fun/formatNumber'
import { getAge } from '../fun/getAge'
import { getBirthDateFallback } from '../fun/getBirthDateFallback'
import { getDataOrThrow } from '../fun/getDataOrThrow'
import { getDeathDateFallback } from '../fun/getDeathDateFallback'
import { getLifeDateFormatted } from '../fun/getLifeDateFormatted'
import { getPersonIdFromUrl } from '../fun/getPersonIdFromUrl'
import { getPersonName } from '../fun/getPersonName'
import { setPageTitle } from '../fun/setPageTitle'
import { _ } from '../model/ccc'
import { FULL_DATE_LENGTH } from '../model/FULL_DATE_LENGTH'
import type { IContemporary } from '../model/IContemporary'
import { Relationship } from '../model/Relationship'
import { ContemporaryLinkComp } from './ContemporaryLinkComp'

export const PagePersonContemporariesComp = defineComponent<
	IShowThenProps<any>
>('PagePersonContemporariesComp', (props, $) => {
	useEffect('page title [ta77p9]', () => {
		setPageTitle(`${getPersonName(getPerson())} kortársai`)
	})

	const data = getDataOrThrow()

	function getPerson() {
		return data.personsById[getPersonIdFromUrl() ?? '']
	}

	const getContemporaries = useMemo('getContemporaries', () => {
		const person = getPerson()
		if (!person) return []
		const birthDate = getBirthDateFallback(data, person)
		const deathDate = getDeathDateFallback(data, person)
		if (!birthDate.value || (deathDate.isFallback && !deathDate.value))
			return []
		const result: IContemporary[] = []
		for (const aPerson of Object.values(data.personsById)) {
			if (aPerson.id === person.id) continue
			const aBirthDate = getBirthDateFallback(data, aPerson)
			let aDeathDate = getDeathDateFallback(data, aPerson)
			if (
				aDeathDate.isFallback &&
				aDeathDate.value &&
				aDeathDate.value.length < FULL_DATE_LENGTH
			) {
				aDeathDate = {
					...aDeathDate,
					value: aDeathDate.value.slice(0, 4) + '-12-31',
				}
			}
			if (
				!aBirthDate.value ||
				(aDeathDate.isFallback
					? aDeathDate.value == null || aDeathDate.value < birthDate.value
					: aDeathDate.value != null && aDeathDate.value < birthDate.value) ||
				(deathDate.value != null && aBirthDate.value > deathDate.value)
			) {
				continue
			}
			const ageDiff = getAge(aBirthDate.value, birthDate.value) ?? 0
			result.push({
				person: aPerson,
				ageDiff: ageDiff,
				birthDate: aBirthDate.value,
				ageAssumed:
					birthDate.isFallback ||
					aBirthDate.isFallback ||
					birthDate.value.length < FULL_DATE_LENGTH ||
					deathDate.isFallback ||
					aDeathDate.isFallback ||
					(deathDate.value != null &&
						deathDate.value.length < FULL_DATE_LENGTH),
			})
		}
		result.sort((a, b) => a.birthDate.localeCompare(b.birthDate))
		return result
	})

	const getContemporaryParditkas = useMemo('getContemporaryParditkas', () =>
		getContemporaries().filter(
			(it) => it.person.relationship === Relationship.Parditka,
		),
	)
	const getContemporaryRelatives = useMemo('getContemporaryRelatives', () =>
		getContemporaries().filter(
			(it) => it.person.relationship !== Relationship.Parditka,
		),
	)

	const state = useState('state', {
		showRelatives: false,
	})

	return (
		<Show
			debugName='Ismert személy'
			it={$when(getPerson, ({ get: getPerson }) => (
				<div class={_.ccc_page}>
					<h1>
						<a href={url`/person/${getPerson().id}`}>
							<b>
								<Slot get={() => getPersonName(getPerson())} />
							</b>
						</a>{' '}
						kortársai
					</h1>
					<p class={_.ccc_date_large}>
						<Slot
							get={() => getLifeDateFormatted({ data, person: getPerson() })}
						/>
						{' – '}
						<Slot
							get={() =>
								getLifeDateFormatted({
									data,
									person: getPerson(),
									isDeath: true,
									defaultFuture: 'mostanáig',
								})
							}
						/>
					</p>
					<h2>
						<b>Parditkák</b> (
						<Slot
							get={() => formatNumber(getContemporaryParditkas().length)}
						/>{' '}
						fő)
					</h2>
					<div class={_.ccc_tiles__centered}>
						<For
							debugName='Parditkák'
							each={getContemporaryParditkas}
							render={ContemporaryLinkComp}
						/>
					</div>
					<h2>
						<b>Rokonok</b> (
						<Slot
							get={() => formatNumber(getContemporaryRelatives().length)}
						/>{' '}
						fő)
					</h2>
					<div class={_.ccc_tiles__centered}>
						<Show
							debugName='Rokonok láthatóak'
							it={$when(
								() => state.showRelatives,
								() => (
									<For
										debugName='Rokonok'
										each={getContemporaryRelatives}
										render={ContemporaryLinkComp}
									/>
								),
							)}
							else={() => (
								<button
									class={_.ccc_button__rounded}
									onclick={() => {
										mutateState($.debugName, 'show relatives [t9w76a]', () => {
											state.showRelatives = true
										})
									}}
								>
									Mutasd a rokonokat
								</button>
							)}
						/>
					</div>
				</div>
			))}
			else={() => <h1>Ez a személy nem található</h1>}
		/>
	)
})
